import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// @mui
import { IconButton, InputAdornment, TextField, Container, Box, Typography, Divider, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../components/iconify';
import { loginUser, setUserFromToken } from '../feature/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { validateInput } from '../utils/validateInput';
import { showErrorToast, showSuccessToast } from '../utils/Toast';
import { startAuthentication } from '@simplewebauthn/browser';
import jwt_decode from 'jwt-decode';
import ApiCall from '../utils/apicall';
import { setAccessToken } from '../utils/authToken';
// ----------------------------------------------------------------------

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [faceIdLoading, setFaceIdLoading] = useState(false);
  const [platformAvailable, setPlatformAvailable] = useState(false);
  // Codex WARNING fix: bind submit button loading/disabled to userSlice.isLoading
  // to prevent duplicate submits while the auth round-trip is in flight.
  const isLoading = useSelector((state) => state.userSlice.isLoading);

  // Detect if this device has a platform authenticator (Face ID, Touch ID, Windows Hello)
  useEffect(() => {
    const PKC = window.PublicKeyCredential;
    if (PKC && typeof PKC.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      PKC.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setPlatformAvailable)
        .catch(() => setPlatformAvailable(false));
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    // Codex WARNING fix: refuse to dispatch a second login while one is in
    // flight. Without this guard, double-click would submit twice and the
    // server would race-process duplicate auth requests.
    if (isLoading) return;
    if (!validateInput('email', email)) return;
    if (!validateInput('password', password)) return;

    dispatch(loginUser({ email, password })).then((res) => {
      if (res.type === 'login-user/auth/fulfilled') return navigate('/', { replace: true });
      if (res.type === 'login-user/auth/rejected') showErrorToast(res.error.message);
    });
  };

  const handleFaceIDLogin = async () => {
    if (!validateInput('email', email)) {
      showErrorToast('Please enter your email address first');
      return;
    }
    setFaceIdLoading(true);
    try {
      // Step 1: Get challenge from server
      const optRes = await ApiCall.post('/webauthn/auth/options', { email });
      const optData = optRes.data?.data;
      if (!optData?.challenge) throw new Error(optData?.message || 'No biometric credential found for this account');

      const { _userId, ...authOptions } = optData;

      // Step 2: Prompt biometric (Face ID / Touch ID / Windows Hello)
      const credential = await startAuthentication(authOptions);

      // Step 3: Verify with server
      const verifyRes = await ApiCall.post('/webauthn/auth/verify', { credential, userId: _userId });
      const { token } = verifyRes.data?.data ?? {};
      if (!token) throw new Error('Verification failed');

      // Step 4: Store auth state and navigate
      const decodedToken = jwt_decode(token);
      setAccessToken(token);
      dispatch(setUserFromToken({ token, decodedToken }));
      navigate('/', { replace: true });
    } catch (err) {
      // NotAllowedError means user cancelled or biometric failed
      if (err?.name === 'NotAllowedError') {
        showErrorToast('Biometric sign-in was cancelled');
      } else {
        showErrorToast(err?.response?.data?.message || err?.message || 'Biometric sign-in failed');
      }
    } finally {
      setFaceIdLoading(false);
    }
  };

  return (
    <Container
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 480,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          marginBottom: '28px',
        }}
      >
        <Box
          component="img"
          src="/assets/sfm-logo.png"
          alt="SFM — Shop Floor Mgmt"
          sx={{ width: 150, height: 'auto', mb: 1 }}
        />
        <Typography
          variant="overline"
          sx={{ letterSpacing: 4, color: 'text.secondary', fontSize: '0.7rem' }}
        >
          A3 Assurance
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Shop Floor Management &amp; Quality Platform
        </Typography>
        {/* Mission tagline per Dean directive 2026-06-23 — mirrors the
            iOS/iPad sign-in mission line. */}
        <Typography
          variant="overline"
          sx={{ mt: 1.5, letterSpacing: 2, color: 'primary.main', fontWeight: 700, fontSize: '0.62rem' }}
        >
          Our Mission
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontStyle: 'italic', color: 'text.secondary', textAlign: 'center', maxWidth: 360 }}
        >
          We Prove the Value of a Quality Management System
        </Typography>
      </Box>

      <Box sx={{ width: '100%', maxWidth: 480 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
          Sign in to your account
        </Typography>

        <TextField
          fullWidth
          name="email"
          label="Email address"
          sx={{ mb: 2 }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <TextField
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Box sx={{ textAlign: 'right', mb: 2 }}>
          <Link to="/reset-password" style={{ fontSize: '0.875rem' }}>
            Forgot Password?
          </Link>
        </Box>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isLoading}
          disabled={isLoading || faceIdLoading}
          onClick={handleLogin}
        >
          Sign In
        </LoadingButton>

        {/* ── Face ID / Biometric sign-in — only shown on supported devices */}
        {platformAvailable && (
          <>
            <Divider sx={{ my: 2.5 }}>
              <Typography variant="body2" sx={{ color: 'text.disabled', px: 1 }}>
                or
              </Typography>
            </Divider>

            <LoadingButton
              fullWidth
              size="large"
              variant="outlined"
              loading={faceIdLoading}
              onClick={handleFaceIDLogin}
              startIcon={
                !faceIdLoading && (
                  <Iconify icon="material-symbols:face-recognition" width={22} />
                )
              }
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.lighter' },
              }}
            >
              Sign in with Face ID / Biometrics
            </LoadingButton>
          </>
        )}

        <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
          Don't have an account?{' '}
          {/* 2026-09-05: was /register, which led to a form whose accounts could
              never log in. Points at the 14-day trial funnel instead. Staff
              accounts are created by an administrator under Users. */}
          <Link to="/signup" style={{ fontWeight: 600, textDecoration: 'none' }}>
            Start a free trial
          </Link>
        </Typography>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.disabled' }}
        >
          © {new Date().getFullYear()} A3 Assurance Inc. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
}
